import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './client';
import type {
  CreateWidgetInput,
  DataEnvelope,
  IdentityLookupSettings,
  TestLookupInput,
  UpdateIdentityLookupInput,
  UpdateWidgetInput,
  Widget,
} from './types';

export const widgetsKey = ['widgets'] as const;

export function useWidgets() {
  return useQuery({
    queryKey: widgetsKey,
    queryFn: ({ signal }) => api<DataEnvelope<Widget[]>>('/widgets', { signal }).then((r) => r.data),
  });
}

export function useSaveWidget() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id?: string; input: CreateWidgetInput | UpdateWidgetInput }) =>
      (id
        ? api<DataEnvelope<Widget>>(`/widgets/${id}`, { method: 'PATCH', body: input })
        : api<DataEnvelope<Widget>>('/widgets', { method: 'POST', body: input })
      ).then((r) => r.data),
    onSuccess: () => void qc.invalidateQueries({ queryKey: widgetsKey }),
  });
}

export function useDeleteWidget() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api<void>(`/widgets/${id}`, { method: 'DELETE' }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: widgetsKey }),
  });
}

export function useWidgetSecret() {
  return useMutation({
    mutationFn: ({ id, rotate }: { id: string; rotate?: boolean }) =>
      (rotate
        ? api<DataEnvelope<{ secret: string }>>(`/widgets/${id}/identity-secret/rotate`, {
            method: 'POST',
            body: {},
          })
        : api<DataEnvelope<{ secret: string }>>(`/widgets/${id}/identity-secret`)
      ).then((r) => r.data.secret),
  });
}

export function useIdentityLookup() {
  return useQuery({
    queryKey: [...widgetsKey, 'identity-lookup'],
    queryFn: ({ signal }) =>
      api<DataEnvelope<IdentityLookupSettings>>('/widgets/identity-lookup', { signal }).then((r) => r.data),
  });
}

export function useUpdateIdentityLookup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateIdentityLookupInput) =>
      api<DataEnvelope<IdentityLookupSettings>>('/widgets/identity-lookup', {
        method: 'PATCH',
        body: input,
      }).then((r) => r.data),
    onSuccess: (data) => qc.setQueryData([...widgetsKey, 'identity-lookup'], data),
  });
}

export function useTestIdentityLookup() {
  return useMutation({
    // The form's current (maybe unsaved) values; omitted ones fall back to the saved setting.
    mutationFn: (input: TestLookupInput) =>
      api<DataEnvelope<Record<string, unknown>>>('/widgets/identity-lookup/test', {
        method: 'POST',
        body: input,
      }).then((r) => r.data),
  });
}
